import { useRouter } from "next/router";
import { useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";

import { formattedMessageParams } from "shared/messages";

import { ROUTES } from "@shared/constants";

const {
  sortOptionsPrice,
  sortOptionsDurationOpportunityCostPrice,
  sortOptionsNumTransfers,
  sortOptionsFlightLength,
  sortOptionsTimeDeparture,
  sortOptionsPlaceDeparture,
  sortOptionsPlaceArrival,
  sortOptionsPriceMinHotel,
  sortOptionsPriceMaxHotel,
  sortOptionsStarsHighToLow,
  sortOptionsStarsLowToHigh,
  sortOptionsRefundableDate,
} = defineMessages({
  sortOptionsPrice: formattedMessageParams("Sorting.price"),
  sortOptionsPriceMinHotel: formattedMessageParams("Sorting.price"),
  sortOptionsRefundableDate: formattedMessageParams("Sorting.refundableDate"),
  sortOptionsPriceMaxHotel: formattedMessageParams("Sorting.maxPrice"),
  sortOptionsStarsHighToLow: formattedMessageParams("Sorting.starsHighToLow"),
  sortOptionsStarsLowToHigh: formattedMessageParams("Sorting.starsLowToHigh"),
  sortOptionsDurationOpportunityCostPrice: formattedMessageParams(
    "Sorting.durationOpportunityCostPrice"
  ),
  sortOptionsNumTransfers: formattedMessageParams("Sorting.numTransfers"),
  sortOptionsFlightLength: formattedMessageParams("Sorting.flightLength"),
  sortOptionsTimeDeparture: formattedMessageParams("Sorting.timeDeparture"),
  sortOptionsPlaceDeparture: formattedMessageParams("Sorting.placeDeparture"),
  sortOptionsPlaceArrival: formattedMessageParams("Sorting.placeArrival"),
});

export type SortTypes =
  | "PRICE"
  | "DURATION_OPPORTUNITY_COST_PRICE"
  | "NUM_TRANSFERS"
  | "FLIGHT_LENGTH"
  | "TIME_DEPARTURE"
  | "PLACE_DEPARTURE"
  | "PLACE_ARRIVAL"
  | "MIN_PRICE_HOTEL"
  | "MAX_PRICE_HOTEL"
  | "STARS_HOTEL_HIGH_TO_LOW"
  | "STARS_HOTEL_LOW_TO_HIGH"
  | "REFUNDABLE_DATE";

type SortOption = {
  value: string;
  label: string;
};

type ReturnType = {
  currentSort: SortTypes;
  changeSortFilter: (sortType: SortTypes) => void;
  sortOption: SortOption;
  sortOptions: SortOption[];
};

const SearchSorting = (isHotels?: boolean): ReturnType => {
  const router = useRouter();
  const intl = useIntl();

  const sortOptions = isHotels
    ? [
        {
          value: "MIN_PRICE_HOTEL",
          label: intl.formatMessage(sortOptionsPriceMinHotel),
        },
        {
          value: "MAX_PRICE_HOTEL",
          label: intl.formatMessage(sortOptionsPriceMaxHotel),
        },
        {
          value: "STARS_HOTEL_HIGH_TO_LOW",
          label: intl.formatMessage(sortOptionsStarsHighToLow),
        },
        {
          value: "STARS_HOTEL_LOW_TO_HIGH",
          label: intl.formatMessage(sortOptionsStarsLowToHigh),
        },
        {
          value: "REFUNDABLE_DATE",
          label: intl.formatMessage(sortOptionsRefundableDate),
        },
      ]
    : [
        {
          value: "PRICE",
          label: intl.formatMessage(sortOptionsPrice),
        },
        {
          value: "DURATION_OPPORTUNITY_COST_PRICE",
          label: intl.formatMessage(sortOptionsDurationOpportunityCostPrice),
        },
        {
          value: "FLIGHT_LENGTH",
          label: intl.formatMessage(sortOptionsFlightLength),
        },
        {
          value: "NUM_TRANSFERS",
          label: intl.formatMessage(sortOptionsNumTransfers),
        },
        {
          value: "TIME_DEPARTURE",
          label: intl.formatMessage(sortOptionsTimeDeparture),
        },
        {
          value: "PLACE_DEPARTURE",
          label: intl.formatMessage(sortOptionsPlaceDeparture),
        },
        {
          value: "PLACE_ARRIVAL",
          label: intl.formatMessage(sortOptionsPlaceArrival),
        },
      ];

  const currentSort = useMemo<SortTypes>(() => {
    if (router.query.sort !== undefined && router.query.sort !== "") {
      return router.query.sort as SortTypes;
    }

    return "PRICE";
  }, [router.query]);

  const changeSortFilter = (sortType: SortTypes) => {
    const newQuery = {
      ...router.query,
      sort: sortType,
    };

    router.replace(
      {
        pathname: isHotels ? ROUTES.HOTELS : ROUTES.RESULTS,
        query: newQuery,
      },
      undefined,
      { shallow: true, scroll: false }
    );
  };

  const sortOption = sortOptions.find((option) => option.value === currentSort);

  return {
    currentSort,
    changeSortFilter,
    sortOption,
    sortOptions,
  };
};

export default SearchSorting;
